import React from 'react';
import { Box, Skeleton, useTheme } from '@material-ui/core';
import { RemoteSvg, ImageWithFallback } from '../..';
import { AvatarSize, IconSize } from '../../../types';
import { SxProps } from '@material-ui/system';

export interface OneToneAvatarProps {
  imagePath?: string;
  avatarSize: AvatarSize;
  iconSize: IconSize;
  iconPath: string;
  bgColour: string;
  altText?: string;
  testId?: string;
  isLoading?: boolean;
}

/**
 * Renders a OneToneAvatar component which first looks to load the optional imagePath, falling back to a OneTone icon.
 * https://share.goabstract.com/72c77a3d-f3c2-4a27-8f5b-288963ab743a?mode=design
 * @param imagePath - the image path
 * @param avatarSize - AvatarSize enum value
 * @param iconSize - IconSize enum value
 * @param iconPath - the full icon path
 * @param bgColour - the desired bg colour which will likely come from a mask property
 * @param altText - optional alt text
 * @param testId - the test id for icon, test id prefix for image
 */
const OneToneAvatar: React.FC<OneToneAvatarProps> = ({
  imagePath,
  avatarSize,
  iconSize,
  iconPath,
  bgColour,
  altText = '',
  testId = 'one-tone-avatar',
  isLoading,
}: OneToneAvatarProps) => {
  const theme = useTheme();

  const wrapperStyles: SxProps = {
    borderRadius: '4px',
    bgcolor: bgColour || theme.palette.primary.dark,
    height: avatarSize,
    width: avatarSize,
  };

  const LoadingSkeleton = (
    <Skeleton
      sx={{ ...wrapperStyles, bgcolor: undefined }}
      animation="wave"
      variant="rectangular"
      data-testid="loading-skeleton"
    />
  );

  const OneToneIcon = (
    <Box
      data-testid={testId}
      sx={{ ...wrapperStyles, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      {!isLoading && <RemoteSvg fillColor={theme.palette.common.white} size={iconSize} fullPath={iconPath} />}
    </Box>
  );

  if (isLoading) return LoadingSkeleton;
  if (!imagePath) return OneToneIcon;
  else
    return (
      <ImageWithFallback
        idPrefix={testId}
        mode="image"
        imagePath={imagePath}
        size={{ height: '100%', width: '100%' }}
        additionalImgStyles={{ objectFit: 'cover', borderRadius: '4px' }}
        wrapperStyles={wrapperStyles}
        altText={altText}
        fallback={OneToneIcon}
      />
    );
};

export default OneToneAvatar;
